import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Container, Paper, Typography, CircularProgress, Alert } from '@mui/material';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../services/supabaseClient';
import { handleOAuthProfile } from '../../services/oauthProfileService';

const AuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const handleAuthCallback = async () => {
      try {
        // Token hash flow (Auth Hook emails use token_hash + type as query params)
        const tokenHash = searchParams.get('token_hash');
        const otpType = searchParams.get('type');

        if (tokenHash && otpType) {
          const { error: verifyError } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: otpType as any,
          });

          if (verifyError) {
            if (mounted) {
              setError(verifyError.message || 'Error al verificar el enlace');
              setIsLoading(false);
              setTimeout(() => navigate('/login'), 3000);
            }
            return;
          }

          if (otpType === 'recovery') {
            navigate('/reset-password');
            return;
          }

          if (otpType === 'signup') {
            navigate('/login', {
              state: { message: 'Email confirmado exitosamente. Por favor inicia sesión.' }
            });
            return;
          }

          // email_change, magic_link, etc.
          await refreshUser();
          navigate('/dashboard', { replace: true });
          return;
        }

        // Check for PKCE code flow (Supabase v2+ email links use code exchange)
        const code = searchParams.get('code');

        if (code) {
          // PKCE flow: exchange the code for a session.
          // Set up an auth state listener BEFORE exchanging so we catch the event type
          // (PASSWORD_RECOVERY vs SIGNED_IN) which tells us how to route.
          const { data: { subscription } } = supabase.auth.onAuthStateChange(
            async (event, session) => {
              // Skip the initial session event — we only care about the post-exchange event
              if (event === 'INITIAL_SESSION') return;
              if (!mounted) { subscription.unsubscribe(); return; }

              subscription.unsubscribe();

              if (event === 'PASSWORD_RECOVERY') {
                navigate('/reset-password');
                return;
              }

              if (event === 'SIGNED_IN' && session) {
                // OAuth or email confirmation
                try {
                  await handleOAuthProfile(session.user);
                } catch (profileError) {
                  console.error('Error handling OAuth profile:', profileError);
                }
                await refreshUser();
                navigate('/dashboard', { replace: true });
                return;
              }

              // Fallback
              navigate('/login');
            }
          );

          const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
          if (exchangeError) {
            subscription.unsubscribe();
            if (mounted) {
              console.error('Code exchange error:', exchangeError);
              setError(exchangeError.message || 'Error al procesar la autenticación');
              setIsLoading(false);
              setTimeout(() => navigate('/login'), 3000);
            }
            return;
          }

          // Safety timeout: if the auth event never fires, redirect to login
          setTimeout(() => {
            if (mounted) {
              subscription.unsubscribe();
              setIsLoading(false);
              navigate('/login');
            }
          }, 5000);

          return; // Wait for onAuthStateChange event
        }

        // Legacy implicit flow: params are in the URL hash fragment
        const hashParams = window.location.hash.substring(1);
        const params = new URLSearchParams(hashParams);

        // Check if this is an email confirmation, password recovery, or OAuth callback
        const type = params.get('type');
        const accessToken = params.get('access_token');
        const providerToken = params.get('provider_token');
        const hashError = params.get('error');
        const errorDescription = params.get('error_description');

        if (hashError) {
          setError(errorDescription || hashError || 'Error al autenticar');
          setIsLoading(false);
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        // Handle OAuth callback (Google, Apple, etc.)
        if (accessToken && providerToken) {
          console.log('OAuth callback detected');

          // Get the current user session
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();

          if (sessionError || !session) {
            throw new Error('No se pudo obtener la sesión del usuario');
          }

          // Handle OAuth profile creation/update
          try {
            await handleOAuthProfile(session.user);
            console.log('OAuth profile handled successfully');
          } catch (profileError) {
            console.error('Error handling OAuth profile:', profileError);
            // Don't block login if profile handling fails
          }

          // Refresh user in context
          await refreshUser();

          // Redirect to dashboard with success message
          navigate('/dashboard', {
            state: { message: 'Inicio de sesión exitoso' },
            replace: true
          });
          return;
        }

        // Handle email confirmation
        if (type === 'signup' && accessToken) {
          // Email confirmation successful
          // Supabase will automatically handle the session
          await refreshUser();

          // Redirect to login with success message
          navigate('/login', {
            state: { message: 'Email confirmado exitosamente. Por favor inicia sesión.' }
          });
          return;
        }

        // Handle password recovery
        if (type === 'recovery' && accessToken) {
          // Password recovery - redirect to reset password page
          // The session is already established by Supabase
          navigate('/reset-password');
          return;
        }

        // No specific action needed, redirect to login
        navigate('/login');
      } catch (err: any) {
        console.error('Auth callback error:', err);
        if (mounted) {
          setError(err.message || 'Error al procesar la autenticación');
          setIsLoading(false);
          setTimeout(() => navigate('/login'), 3000);
        }
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    handleAuthCallback();

    return () => { mounted = false; };
  }, [navigate, refreshUser, searchParams]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {isLoading ? (
            <>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Procesando autenticación...
              </Typography>
            </>
          ) : error ? (
            <>
              <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                {error}
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Redirigiendo al inicio de sesión...
              </Typography>
            </>
          ) : (
            <Typography variant="body1" color="text.secondary">
              Redirigiendo...
            </Typography>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default AuthCallback;
